// src/private/src/pages/app/clients/components/EmptyState.jsx
import React, { memo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/**
 * Contextual empty state component that replaces generic "No data" messages
 * with helpful guidance about why data might be missing and what to do.
 */

const CONTEXT_MESSAGES = {
  verticals: {
    title: 'No vertical performance data yet',
    message: 'Vertical breakdowns appear once campaigns have been running and data has been collected for the selected date range.',
  },
  channels: {
    title: 'No channel performance data yet',
    message: 'Channel data populates after ad platform data has been imported. This typically updates daily.',
  },
  territories: {
    title: 'No territory data available',
    message: 'Territory breakdowns require ZIP code coverage to be configured. Check Client Zips to set up territories.',
  },
  states: {
    title: 'No state-level data',
    message: 'State breakdowns appear once leads have been geocoded to their respective states.',
  },
  dma: {
    title: 'No DMA data available',
    message: 'DMA breakdowns require geographic resolution of leads. Data appears after campaign data has been imported.',
  },
  cities: {
    title: 'No city performance data',
    message: 'City-level data becomes available after leads have been geocoded. Try expanding the date range.',
  },
  counties: {
    title: 'No county performance data',
    message: 'County-level data becomes available after leads have been geocoded. Try expanding the date range.',
  },
  leads: {
    title: 'No leads found',
    message: 'No leads match your current filters and date range. Try broadening your search or adjusting the date range.',
  },
  mom: {
    title: 'No month-over-month data',
    message: 'MoM data requires at least one full month of campaign data. Data populates as monthly summaries are generated.',
  },
  dod: {
    title: 'No daily data available',
    message: 'Day-over-day data appears after campaign data has been imported for the selected date range.',
  },
  generic: {
    title: 'No data available',
    message: 'Data for this section is not yet available for the selected date range. Try adjusting the dates or check back later.',
  },
};

function EmptyState({ context = 'generic', colSpan = 1, inline = false }) {
  const { title, message } = CONTEXT_MESSAGES[context] || CONTEXT_MESSAGES.generic;

  if (inline) {
    return (
      <Box sx={{ py: 3, px: 2, textAlign: 'center' }}>
        <Stack spacing={0.5} alignItems="center">
          <InfoOutlinedIcon sx={{ fontSize: 28, color: 'text.disabled' }} />
          <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ maxWidth: 400 }}>
            {message}
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <tr>
      <td colSpan={colSpan} style={{ textAlign: 'center', padding: '24px 16px' }}>
        <Stack spacing={0.5} alignItems="center">
          <InfoOutlinedIcon sx={{ fontSize: 28, color: 'text.disabled' }} />
          <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ maxWidth: 400 }}>
            {message}
          </Typography>
        </Stack>
      </td>
    </tr>
  );
}

export default memo(EmptyState);
